import { retrieveRawInitData } from '@telegram-apps/sdk-react';
import { IS_DEV_PREVIEW, mockApiResponse } from './devPreviewApi';

const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  console.warn('[api] VITE_API_URL is not set — Worker calls will fail');
}

/**
 * Thrown when the Worker rejects a request with `401 expired` — i.e. the
 * Telegram-issued `auth_date` in the user's initData is older than the
 * Worker's `INITDATA_MAX_AGE_SEC` window. initData is generated on Mini App
 * launch and never refreshed by the SDK; the only fix is for the user to
 * close + reopen the Mini App from the bot menu.
 *
 * UI handles this via a global modal — see SessionExpiredModal + the
 * onSessionExpired listener registered in App.tsx.
 */
export class SessionExpiredError extends Error {
  constructor(detail: string) {
    super(`session expired: ${detail}`);
    this.name = 'SessionExpiredError';
  }
}

// ─────────────────────────────────────────────────────────────────────
// Session-expired event bus + persistent counter
// ─────────────────────────────────────────────────────────────────────
// Pub-sub so the modal can subscribe once at App level and any API call
// site can fire without prop-drilling.
//
// Counter (`attempt`) persists across Mini App close/reopen cycles via
// localStorage. Increments on every expired event; resets when a fresh
// session is confirmed (successful /api/me at AppGate). Lets the UI tell
// the user "you've already tried to reopen — Telegram is caching your
// session, fully quit Telegram and try again" on attempt >= 2.

const COUNTER_KEY = 'dk:session_expired_count';

function readCounter(): number {
  try {
    return Number(localStorage.getItem(COUNTER_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}

function writeCounter(n: number): void {
  try {
    if (n === 0) localStorage.removeItem(COUNTER_KEY);
    else localStorage.setItem(COUNTER_KEY, String(n));
  } catch {
    /* localStorage may be disabled in private mode — graceful skip */
  }
}

/**
 * Reset the session-expired attempt counter. Call when a fresh session is
 * confirmed — e.g. AppGate's /api/me succeeds, meaning Telegram delivered
 * fresh initData and the user is back to a working state.
 */
export function resetSessionExpiredCount(): void {
  writeCounter(0);
}

type SessionExpiredListener = (detail: string, attempt: number) => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

/** Subscribe to session-expired events. Returns an unsubscribe fn. */
export function onSessionExpired(fn: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(fn);
  return () => {
    sessionExpiredListeners.delete(fn);
  };
}

function notifySessionExpired(detail: string, attempt: number): void {
  for (const fn of sessionExpiredListeners) {
    try {
      fn(detail, attempt);
    } catch (e) {
      console.error('[api] session-expired listener threw', e);
    }
  }
}

/**
 * Authenticated fetch — attaches `Authorization: tma <initDataRaw>` header.
 * The Worker's auth middleware validates the HMAC signature on every request.
 *
 * On `401 expired` (stale auth_date), notifies registered listeners and
 * throws SessionExpiredError so callers can treat it specially.
 */
async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Short-circuit in `?dev=1` mode: return canned responses so all routes
  // render without needing the Worker / ngrok / Postgres stack running.
  // mock-env.ts handles the Telegram SDK side; this handles the API side.
  // See lib/devPreviewApi.ts for the mock data + limitations (Pay flow
  // won't complete end-to-end in dev preview).
  if (IS_DEV_PREVIEW) {
    return mockApiResponse(path, init);
  }

  const initData = retrieveRawInitData();
  if (!initData) {
    throw new Error('No Telegram initData available — open the app from Telegram.');
  }
  const headers = new Headers(init.headers);
  headers.set('Authorization', `tma ${initData}`);
  const r = await fetch(`${API_URL}${path}`, { ...init, headers });

  // Detect session-expired specifically (401 with detail starting with "expired").
  // Other 401s (hmac_mismatch, missing_hash, not_whitelisted → 403) bubble up
  // as generic errors — they aren't actionable via reopen.
  if (r.status === 401) {
    const peek = await r.clone().json().catch(() => null) as { detail?: string } | null;
    const detail = peek?.detail ?? '';
    if (typeof detail === 'string' && detail.startsWith('expired')) {
      const attempt = readCounter() + 1;
      writeCounter(attempt);
      notifySessionExpired(detail, attempt);
      throw new SessionExpiredError(detail);
    }
  }

  return r;
}

async function safeJson(r: Response): Promise<{ detail?: string } | null> {
  try {
    return (await r.json()) as { detail?: string };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────
export interface User {
  tg_id: number;
  username: string | null;
  first_name: string;
  last_name: string | null;
  language_code: string | null;
  is_premium: boolean;
  created_at: string;
  updated_at: string;
}

export type GenerationStatus = 'pending' | 'processing' | 'done' | 'failed';
export type KissType = 'cheek' | 'lips' | 'french';

export interface Generation {
  id: string;
  status: GenerationStatus;
  kiss_type: KissType;
  duration_sec: number;
  scene: string;
  output_video: string | null;
  output_video_url: string | null;
  /**
   * Intermediate pair-preview frame R2 key. Set when the image-gen phase
   * (kie.ai Nano Banana) completes — drives the "image ready" check on
   * the Generating page's 2-phase track.
   *
   * Only returned by GET /:id, not by the list endpoint — hence optional.
   */
  pair_image_key?: string | null;
  /** Public URL for `pair_image_key` (null if not yet produced). */
  pair_image_url?: string | null;
  /**
   * Per-step retry counters. 0 = not yet started, 1 = first attempt
   * in flight or done, 2 = second attempt (retry) in flight or done.
   * Frontend uses these to show a "retrying" banner when attempts > 1.
   *
   * Only returned by GET /:id, hence optional.
   */
  image_attempts?: number;
  video_attempts?: number;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface CreateGenerationParams {
  photo1: File;
  photo2: File;
  kiss_type?: KissType;
  duration_sec?: number;
  scene?: string;
}

// ─────────────────────────────────────────────────────────────────────
// API methods
// ─────────────────────────────────────────────────────────────────────

/**
 * Fetch (and upsert) the current Telegram user.
 */
export async function getMe(): Promise<User> {
  const r = await authFetch('/api/me');
  if (!r.ok) {
    const body = await safeJson(r);
    throw new Error(`getMe failed (${r.status}): ${body?.detail ?? r.statusText}`);
  }
  return r.json() as Promise<User>;
}

export interface CreateGenerationResult {
  id: string;
  status: GenerationStatus;
  kiss_type: KissType;
  duration_sec: number;
  scene: string;
  cost_stars: number;
  /** Telegram Stars invoice URL — pass to openInvoice() from the SDK. */
  invoice_url: string;
}

/**
 * Create a new generation: uploads photos, returns an invoice URL.
 * Frontend then calls openInvoice() and on `paid` navigates to /generating.
 * The actual kie.ai pipeline starts on the Worker side when the
 * successful_payment webhook lands.
 */
export async function createGeneration(
  params: CreateGenerationParams,
): Promise<CreateGenerationResult> {
  const fd = new FormData();
  fd.set('photo1', params.photo1);
  fd.set('photo2', params.photo2);
  fd.set('kiss_type', params.kiss_type ?? 'lips');
  fd.set('duration_sec', String(params.duration_sec ?? 10));
  fd.set('scene', params.scene ?? 'cozy cafe');

  const r = await authFetch('/api/generations', { method: 'POST', body: fd });
  if (!r.ok) {
    const body = await safeJson(r);
    throw new Error(`createGeneration failed (${r.status}): ${body?.detail ?? r.statusText}`);
  }
  return r.json() as Promise<CreateGenerationResult>;
}

/**
 * Get the current state of a generation. Frontend should poll this every
 * few seconds while in the "Generating" screen.
 */
export async function getGeneration(id: string): Promise<Generation> {
  const r = await authFetch(`/api/generations/${id}`);
  if (!r.ok) {
    const body = await safeJson(r);
    throw new Error(`getGeneration failed (${r.status}): ${body?.detail ?? r.statusText}`);
  }
  return r.json() as Promise<Generation>;
}

export interface ListGenerationsResult {
  items: Generation[];
  has_more: boolean;
}

/** List the current user's generation history. */
export async function listGenerations(opts: { limit?: number; before?: string } = {}): Promise<ListGenerationsResult> {
  const qs = new URLSearchParams();
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.before) qs.set('before', opts.before);
  const r = await authFetch(`/api/generations${qs.toString() ? '?' + qs : ''}`);
  if (!r.ok) {
    const body = await safeJson(r);
    throw new Error(`listGenerations failed (${r.status}): ${body?.detail ?? r.statusText}`);
  }
  return r.json() as Promise<ListGenerationsResult>;
}

export interface Model {
  id: string;
  name: string;
  photo_key: string;
  tags: string[];
}

/** Browse the model catalog. */
export async function listCatalog(): Promise<Model[]> {
  const r = await authFetch('/api/catalog');
  if (!r.ok) {
    const body = await safeJson(r);
    throw new Error(`listCatalog failed (${r.status}): ${body?.detail ?? r.statusText}`);
  }
  const j = (await r.json()) as { models: Model[] };
  return j.models;
}
